import { useCallback } from 'react';

import { ContactChannel, IContact } from 'entities/Team/types';
import { formatPhone } from 'utils/formatPhone';

type ContactLinkProps = {
  href: string;
  target?: string;
  rel?: string;
};

type ContactLinkResult = ContactLinkProps & {
  displayValue?: string;
};

const externalLinkProps: Pick<ContactLinkProps, 'target' | 'rel'> = {
  target: '_blank',
  rel: 'noopener noreferrer',
};

const normalizePhoneValue = (value: string) => {
  const trimmed = value.trim();
  let digits = trimmed.replace(/\D/g, '');

  if (!digits) {
    return { hrefValue: trimmed, displayValue: trimmed };
  }

  if (digits.length === 11 && digits.startsWith('8')) {
    digits = `7${digits.slice(1)}`;
  } else if (digits.length === 10) {
    digits = `7${digits}`;
  }

  const hasPlusPrefix = trimmed.startsWith('+');
  const numeric = digits;
  const international = hasPlusPrefix ? `+${numeric}` : numeric;
  const hrefValue = international.startsWith('+') ? international : `+${international}`;
  const displayValue = formatPhone(hrefValue);

  return { hrefValue, displayValue };
};

const buildPhoneLink = (value: string): ContactLinkResult => {
  if (value.startsWith('tel:')) {
    const raw = value.replace(/^tel:/i, '');
    const { hrefValue, displayValue } = normalizePhoneValue(raw);

    return {
      href: `tel:${hrefValue}`,
      displayValue,
    };
  }

  const { hrefValue, displayValue } = normalizePhoneValue(value);

  return {
    href: `tel:${hrefValue}`,
    displayValue,
  };
};

const buildEmailLink = (value: string): ContactLinkResult => {
  if (value.startsWith('mailto:')) {
    return { href: value };
  }

  return { href: `mailto:${value}` };
};

const buildTelegramLink = (value: string): ContactLinkResult => {
  if (/^https?:\/\//i.test(value)) {
    return { href: value, ...externalLinkProps };
  }

  const trimmed = value.trim();
  const digitsOnly = trimmed.replace(/\D/g, '');

  if (digitsOnly.length > 0) {
    return { href: `https://t.me/+${digitsOnly}`, ...externalLinkProps };
  }

  const username = trimmed.replace(/^@/, '');

  return { href: `https://t.me/${username}`, ...externalLinkProps };
};

const buildWhatsappLink = (value: string): ContactLinkResult => {
  if (/^https?:\/\//i.test(value)) {
    return { href: value, ...externalLinkProps };
  }

  const digits = value.replace(/\D/g, '');
  const destination = digits.length > 0 ? digits : value.trim();

  return { href: `https://wa.me/${destination}`, ...externalLinkProps };
};

export const useContactLink = () => {
  return useCallback((contact?: IContact | null): ContactLinkResult | null => {
    if (!contact) {
      return null;
    }

    const raw = contact.contact.trim();

    if (!raw) {
      return null;
    }

    switch (contact.contactChannel) {
      case ContactChannel.phone:
        return buildPhoneLink(raw);
      case ContactChannel.email:
        return buildEmailLink(raw);
      case ContactChannel.telegram:
        return buildTelegramLink(raw);
      case ContactChannel.whatsapp:
        return buildWhatsappLink(raw);
      default:
        return null;
    }
  }, []);
};

export type { ContactLinkProps, ContactLinkResult };
